import { motion } from 'framer-motion';
import { Wallet, Store, ExternalLink, FileText } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import PageHero from '../components/PageHero';
import PathSteps from '../components/PathSteps';
import { SF_UI } from '../components/ui/tokens';
import {
  CLASSICS_FEATURES,
  CLASSICS_HEADER,
  CLASSICS_LINKS,
  CLASSICS_STEPS,
  CLASSICS_TRANSITION,
  SFUT_NFT_CONTRACT,
} from '../data/classicsPath';

const handleNativeShare = async () => {
  const shareData = {
    title: 'SportsFuture Classics',
    text: CLASSICS_HEADER,
    url: typeof window !== 'undefined' ? `${window.location.origin}/legacy/classics` : 'https://sportsfuture.io/legacy/classics',
  };
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share(shareData);
      return;
    }
  } catch {
    /* user cancelled or share failed — fall through */
  }
  const tweet = `https://x.com/intent/tweet?text=${encodeURIComponent(`${shareData.text} ${shareData.url}`)}`;
  window.open(tweet, '_blank', 'noopener,noreferrer');
};

const ClassicsPage = () => {
  return (
    <>
      <PageHero
        eyebrow="Legacy · Classics"
        title="Classics"
        subtitle={CLASSICS_HEADER}
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
        <div className="max-w-4xl mx-auto">
          {/* Header connection points: wallet + SF smart contract */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`${SF_UI.surfaceCard} ${SF_UI.surfaceCardPad} mb-16 border-primary/15 bg-gradient-to-br from-primary-50 via-white to-white`}
          >
            <h2 className="text-lg font-semibold text-center mb-2">Connect to SportsFuture on-chain</h2>
            <p className="text-gray-600 text-center text-sm mb-6 max-w-2xl mx-auto">
              Classics is powered by the SportsFuture wallet context and the live Olympic Series
              collection on Apertum — the Olympics artwork is the collection, not a placeholder.
              Use these links to open the contract, marketplace, and self-custodial wallet path.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                href={CLASSICS_LINKS.metaMask}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
                size="md"
              >
                <Wallet size={18} className="mr-2" />
                Self-custodial wallet
              </Button>
              <Button
                href={CLASSICS_LINKS.apertumExplorerNft}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                size="md"
              >
                <FileText size={18} className="mr-2" />
                Olympic Series contract
              </Button>
              <Button
                href={CLASSICS_LINKS.openPlaza}
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                size="md"
              >
                <Store size={18} className="mr-2" />
                OpenPlaza marketplace
              </Button>
            </div>
            <p className="mt-4 text-center text-xs text-gray-500 break-all">
              Contract: {SFUT_NFT_CONTRACT}
            </p>
          </motion.div>

          {/* Three feature pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {CLASSICS_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system border border-gray-100 flex flex-col"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                      <Icon size={32} />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-center flex-1 mb-5">{feature.body}</p>
                  <a
                    href={feature.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center text-sm font-medium text-primary hover:text-primary-600 transition-colors"
                  >
                    {feature.ctaLabel}
                    <ExternalLink size={14} className="ml-1.5" />
                  </a>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center text-lg text-gray-700 italic border-t border-gray-200 pt-10"
          >
            {CLASSICS_TRANSITION}
          </motion.p>
        </div>
      </Section>

      {/* Five-step click path */}
      <Section bgColor="bg-primary-50" title="Your Classics path" subtitle="Five simple clicks from wallet to mint to share." centered>
        <PathSteps steps={CLASSICS_STEPS} onShare={handleNativeShare} />

        <div className="text-center mt-12">
          <Button
            href={CLASSICS_LINKS.openPlaza}
            target="_blank"
            rel="noopener noreferrer"
            variant="primary"
            size="lg"
          >
            Start on OpenPlaza
            <ExternalLink size={18} className="ml-2" />
          </Button>
        </div>
      </Section>
    </>
  );
};

export default ClassicsPage;
